import React, {memo, useEffect, useState} from "react";
import {Handle, Position, Node, useReactFlow} from "@xyflow/react";
import {Icon} from "@/components/ui/icon";
import {WorkflowNodeData, WorkflowNodeSetting} from "@/types/node";
import {generateId, getTailwindColor} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";

import {WorkflowNodesData} from "./NodesData";
import NodeWithToolbar from "../components/NodeToolbar";
import {useSelectedElements} from "@/hooks/useSelectedElements";

export function createNode(blueprint: Partial<Node>): Node<WorkflowNodeData> {
  const {
    id = generateId(),
    type,
    data = {},
    position = {x: 0, y: 0},
  } = blueprint;

  return {
    id,
    type,
    data,
    position,
  } as Node<WorkflowNodeData>;
}

const JumpNode = ({
  id,
  data,
  showContext,
}: {
  id: string;
  data: WorkflowNodeData;
  showContext: boolean;
}) => {
  const {getSelectedNode} = useSelectedElements();
  const {deleteElements, getNode} = useReactFlow();

  const selectedNode = getSelectedNode();
  const isNodeSelected = selectedNode?.id === id;
  const [isToolbarVisible, setIsToolbarVisible] = useState(isNodeSelected);

  const nodeConfig =
    WorkflowNodesData[data.type as keyof typeof WorkflowNodesData];

  const contextColor = getTailwindColor(nodeConfig.styles.contextColor);
  const nodeStyles = {
    icon: {
      color: nodeConfig.styles.iconColor,
      backgroundColor: nodeConfig.styles.iconBackgroundColor,
      borderColor: nodeConfig.styles.iconBorderColor,
    },
  };

  const jumpToStepNodeId = getSettingValueByKey(
    data.settings ?? [],
    "jump_to_step",
  );
  const jumpToStepNode = getNode(jumpToStepNodeId as string);
  const jumpToStepNodeSettings = jumpToStepNode?.data
    .settings as WorkflowNodeSetting[];

  const jumpToStepNodeTitle =
    getSettingValueByKey(jumpToStepNodeSettings ?? [], "name") ||
    jumpToStepNode?.data.settingsTitle;

  useEffect(() => {
    setIsToolbarVisible(isNodeSelected);
  }, [isNodeSelected]);

  if (!nodeConfig) {
    console.error(`No configuration found for node type: ${data.type}`);
    return null;
  }
  return (
    <div
      onMouseEnter={() => setIsToolbarVisible(true)}
      onMouseLeave={() => !isNodeSelected && setIsToolbarVisible(false)}
    >
      <div
        className={`node w-85 bg-white border rounded-lg shadow-node transition-all ${showContext ? "enableContext" : ""}`}
        style={
          showContext
            ? ({
                "--overlay-color": contextColor,
              } as React.CSSProperties)
            : {}
        }
      >
        <div className="flex items-center gap-3 px-3 py-3.5">
          <Icon className={"border"} style={nodeStyles.icon}>
            {nodeConfig.icon &&
              React.createElement(nodeConfig.icon, {
                color: nodeStyles.icon.color,
              })}
          </Icon>
          <div className="leading-none">
            <h3 className="text-base font-medium hasContext w-fit">Jump to</h3>
            {jumpToStepNodeTitle !== null && (
              <span className="text-sm text-secondary hasContext">
                {jumpToStepNodeTitle as string}
              </span>
            )}
          </div>
        </div>
        <NodeWithToolbar
          nodeId={id}
          nodeType={data.type}
          isToolbarVisible={isToolbarVisible}
          onDelete={() => deleteElements({nodes: [{id}]})}
          onMouseEnter={() => setIsToolbarVisible(true)}
          onMouseLeave={() => isNodeSelected && setIsToolbarVisible(false)}
        />
      </div>

      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className={"handle"}
        id={`${id}-top`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className={"handle"}
        id={`${id}-bottom`}
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={true}
        className={"handle"}
        id={`${id}-right-source`}
      />
      <Handle
        type="source"
        position={Position.Left}
        isConnectable={true}
        className={"handle"}
        id={`${id}-left-target`}
      />
    </div>
  );
};

export default memo(JumpNode);
